import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Car, Eye, EyeOff, Loader2 } from "lucide-react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const { user, login } = useAuth();
  const navigate = useNavigate();

  const handleForgotPassword = async () => {
    if (resetLoading) return;
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) {
      toast.error("Saisissez d'abord votre email dans le champ ci-dessus");
      return;
    }
    setResetLoading(true);
    try {
      const res = await api.post("/auth/forgot-password", { email: trimmedEmail });
      const recipient = res.data?.recipient || trimmedEmail;
      if (res.data?.email_sent) {
        toast.success(`Email de réinitialisation envoyé à ${recipient}`);
      } else {
        toast.info(
          `Si un compte existe pour ${trimmedEmail}, un lien de réinitialisation vient d'y être envoyé.`
        );
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Impossible d'envoyer la demande";
      toast.error(message);
    } finally {
      setResetLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      const routes = {
        admin: "/admin",
        hr: "/admin",
        manager: "/manager",
        seller: "/seller"
      };
      navigate(routes[user.role] || "/");
    }
  }, [user, navigate]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await api.post("/seed");
      toast.success("Données de test initialisées !");
    } catch (error) {
      if (error.response?.data?.message === "Données déjà initialisées") {
        toast.info("Les données de test existent déjà");
      } else {
        toast.error("Erreur lors de l'initialisation");
      }
    } finally {
      setSeeding(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Validation côté client
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedPassword = password.trim();
    
    if (!trimmedEmail || !trimmedPassword) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    
    setLoading(true);

    try {
      const loggedUser = await login(trimmedEmail, trimmedPassword);
      toast.success(`Bienvenue, ${loggedUser.name} !`);
      
      const routes = {
        admin: "/admin",
        hr: "/admin",
        manager: "/manager",
        seller: "/seller"
      };
      
      // Petit délai pour laisser le storage se synchroniser
      setTimeout(() => {
        navigate(routes[loggedUser.role] || "/", { replace: true });
      }, 100);
    } catch (error) {
      console.error('Erreur connexion:', error);
      const message = error.response?.data?.detail || 
                      error.message || 
                      "Identifiants incorrects";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Form Section */}
      <div className="login-form-section">
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <img 
              src="/logo-starter.png" 
              alt="Starter Logo" 
              className="w-14 h-14 object-contain"
            />
            <div>
              <h1 
                className="text-2xl font-bold text-[#212E38]"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                Starter
              </h1>
              <p className="text-sm text-gray-500">by Verbaere Performance Suite</p>
            </div>
          </div>

          {/* Title */}
          <div className="mb-8">
            <h2 
              className="text-3xl font-bold text-[#212E38] mb-2"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              Connexion
            </h2>
            <p className="text-gray-500">
              Accédez à votre espace de gestion des objectifs
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5" autoComplete="on" method="post">
            <div className="space-y-2">
              <Label htmlFor="email">Adresse email</Label>
              <Input
                id="email"
                type="email"
                name="email"
                autoComplete="email username"
                placeholder="votre@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={(e) => setEmail(e.target.value.trim())}
                required
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                className="h-12 bg-gray-50 border-gray-200 focus:bg-white"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck="false"
                  className="h-12 bg-gray-50 border-gray-200 focus:bg-white pr-12"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 btn-primary text-base"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Connexion...
                </>
              ) : (
                "Se connecter"
              )}
            </Button>

            <div className="text-center pt-1">
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={resetLoading}
                className="text-sm text-gray-500 hover:text-[#212E38] underline underline-offset-2 disabled:opacity-50"
                data-testid="forgot-password-btn"
              >
                {resetLoading ? "Envoi..." : "Mot de passe oublié ?"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Image Section */}
      <div className="login-image-section login-image-navy">
        <img
          src="https://images.pexels.com/photos/15668129/pexels-photo-15668129.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Skoda véhicule"
        />
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="text-center text-white p-8">
            <h2 
              className="text-4xl font-bold mb-4"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              Groupe Verbaere
            </h2>
            <p className="text-xl text-white/80">
              Gestion des objectifs commerciaux
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
